import React, { useState } from "react";
import { AuditTxn, useAuth, authorizedAxios } from "@moonscape/shared";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ms";
import { message } from "@/components/ui/toast";

interface AuditErrorAlertProps {
  error: AuditTxn;
  className?: string;
  onActionComplete?: () => void;
}

/**
 * Displays audit error messages with optional context suggestions.
 * Shows error_msg by default, and error_context.suggested_resolution if available.
 * For suspicious contract interactions, displays action buttons to mark as theft/loss or dismiss.
 */
export const AuditErrorAlert: React.FC<AuditErrorAlertProps> = ({
  error,
  className = "",
  onActionComplete,
}) => {
  const hasContext = error.error_context?.suggested_resolution;
  const errorMessage = error.error_msg;
  const suggestedResolution = error.error_context?.suggested_resolution;
  const possibleActions = error.error_context?.possible_actions;
  const isSuspiciousContract = error.error_type === "unknown-contract-interaction";

  const [isProcessing, setIsProcessing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const { getToken } = useAuth();

  if (!errorMessage || isDismissed) {
    return null;
  }

  const handleMarkAsLoss = async () => {
    if (!error.ext_txn_id) {
      message.error("Transaction ID not found");
      return;
    }

    setIsProcessing(true);
    try {
      const token = await getToken();
      await authorizedAxios(token).post(
        `/transaction/${error.ext_txn_id}/mark-as-loss`
      );
      message.success("Transaction marked as loss write-off");
      setIsDismissed(true);
      onActionComplete?.();
    } catch (err) {
      message.error("Failed to update transaction");
      console.error("Error marking as loss:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkLegitimate = async () => {
    if (!error.ext_txn_id) {
      message.error("Transaction ID not found");
      return;
    }

    setIsProcessing(true);
    try {
      const token = await getToken();
      await authorizedAxios(token).post(
        `/transaction/${error.ext_txn_id}/mark-as-legitimate`
      );
      message.success("Transaction marked as legitimate");
      setIsDismissed(true);
      onActionComplete?.();
    } catch (err) {
      message.error("Failed to update transaction");
      console.error("Error marking as legitimate:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIgnoreWarning = () => {
    message.info("Warning dismissed for this session");
    setIsDismissed(true);
  };

  return (
    <Callout
      tone="problem"
      title={error.error_type?.replace(/-/g, " ") || "Audit error"}
      className={className}
    >
      {errorMessage}

      {/* Suggested Resolution (if available) */}
      {hasContext && (
        <div className="mt-3 border-t border-ms-line pt-3">
          <p className="text-ms-ink">How to fix</p>
          <p className="mt-1">{suggestedResolution}</p>
        </div>
      )}

      {/* Action Buttons for Suspicious Contract Interactions */}
      {isSuspiciousContract && possibleActions && (
        <div className="mt-4 border-t border-ms-line pt-3">
          <p className="mb-3 text-ms-ink">What would you like to do?</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleMarkAsLoss}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Mark as Theft/Loss"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkLegitimate}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Mark as Legitimate"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleIgnoreWarning} disabled={isProcessing}>
              Ignore Warning
            </Button>
          </div>
          <p className="mt-2 text-ms-dim">
            <b>Note:</b> Marking as theft/loss will set the transaction type to "loss_writeoff" with
            $0 proceeds.
          </p>
        </div>
      )}
    </Callout>
  );
};

export default AuditErrorAlert;
